import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { WIDGET_REGISTRY } from './widgetRegistry';
import './DashboardGrid.css';

const COLS = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
const ROW_HEIGHT = 80;

/**
 * Renders the customizable dashboard grid.
 *
 * Props:
 *   layout         – array of react-grid-layout items { i, x, y, w, h }
 *   visibleWidgets – Set of widget IDs to render
 *   onLayoutChange – (newLayout) => void
 *   widgetProps    – object passed as props to every widget's render fn
 */
function DashboardGrid({ layout, visibleWidgets, onLayoutChange, widgetProps = {} }) {
  const [draggingId, setDraggingId] = useState(null);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  // Measure container width so ResponsiveGridLayout can calculate breakpoints
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      if (entries[0]) setContainerWidth(entries[0].contentRect.width);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const handleLayoutChange = useCallback((newLayout) => {
    if (onLayoutChange) onLayoutChange(newLayout);
  }, [onLayoutChange]);

  // Merge registry minW/minH into each layout item so the grid enforces them
  const visibleItems = layout
    .filter(item => visibleWidgets.has(item.i))
    .map(item => {
      const def = WIDGET_REGISTRY[item.i];
      if (!def) return item;
      return {
        ...item,
        minW: def.minW ?? 2,
        minH: def.minH ?? 2,
      };
    });

  return (
    <div ref={containerRef} className="dashboard-grid-root">
      <ResponsiveGridLayout
        className="layout"
        layouts={{ lg: visibleItems }}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={COLS}
        rowHeight={ROW_HEIGHT}
        width={containerWidth}
        onLayoutChange={handleLayoutChange}
        onDragStart={(_, item) => setDraggingId(item.i)}
        onDragStop={() => setDraggingId(null)}
        draggableHandle=".widget-drag-handle"
        resizeHandles={['se', 'sw', 'ne', 'nw', 's', 'e']}
        margin={[8, 8]}
        containerPadding={[8, 8]}
        isResizable
        isDraggable
      >
        {visibleItems.map(item => {
          const def = WIDGET_REGISTRY[item.i];
          if (!def) return null;

          return (
            <div key={item.i} className={`widget-cell ${draggingId === item.i ? 'dragging' : ''}`}>
              <div className="widget-drag-handle" title="Drag to move">⠿</div>
              <div className="widget-content">
                {def.render(widgetProps)}
              </div>
            </div>
          );
        })}
      </ResponsiveGridLayout>
    </div>
  );
}

export default DashboardGrid;
